import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { decode } from 'he';
import { useAccount } from '../../context/AccountContext';
import {
  calendarPostHref,
  channelMark,
  channelName,
  dayTime,
  postText,
  postTime,
  REACH_ARTICLE_POSTS,
  toDate,
  type ReachPost,
} from './reach';
import { TONE } from './PostPanel';
import { WritePost, imageFor } from './WritePost';
import { AgentFirstButton } from '../cms/AskYourAgent';

/**
 * The article screen's Reach card (Prototype "Article posts", feedback
 * 3dc485ba): the posts that link to this article — channel, words, where each
 * stands and when — each opening on the Calendar, and "Write a post about
 * this", which opens the composer with the article's link, summary and main
 * image filled in. An article that isn't live yet starts on a time after it
 * goes live. Without Reach, a pointer to it.
 */

const SHOWN = 4;
const HOUR_MS = 60 * 60 * 1000;

export interface ReachArticle {
  id: string;
  title: string;
  summary?: string | null;
  /** The main image (none for a video). */
  imageUrl?: string | null;
  publishStatus?: string | null;
  /** When it went live, or goes live when scheduled. */
  publishedDate?: string | null;
}

// Feed summaries arrive as HTML with entities; a post is plain text.
const plain = (v?: string | null) =>
  decode(String(v || '').replace(/<[^>]*>/g, ' '))
    .replace(/\s+/g, ' ')
    .trim();

export function ArticleReachCard({
  reachOn,
  article,
}: {
  reachOn: boolean;
  article: ReachArticle | null;
}) {
  const { accountId } = useAccount();
  const [writing, setWriting] = useState(false);
  const { data, loading, refetch } = useQuery<{
    reachArticlePosts: { link: string | null; posts: ReachPost[] };
  }>(REACH_ARTICLE_POSTS, {
    variables: { accountId, articleId: article?.id },
    skip: !reachOn || !accountId || !article?.id,
    fetchPolicy: 'cache-and-network',
  });

  if (!reachOn || !article) {
    return (
      <div className="ink-rail-block">
        <div className="ink-rail-label">Social</div>
        <div className="ink-rail-note">
          Share new articles on your social channels: your agent drafts a post per channel when an
          article goes live.
        </div>
        <Link to="/addons" className="ink-link-btn ark-more">
          About Social →
        </Link>
      </div>
    );
  }

  const posts = data?.reachArticlePosts.posts || [];
  const live = article.publishStatus === 'published';
  const goesLive = article.publishStatus === 'scheduled' ? toDate(article.publishedDate) : null;

  return (
    <div className="ink-rail-block" data-part="reach">
      <div className="ink-rail-head ark-head">
        <div className="ink-rail-label">Social</div>
        {posts.length ? (
          <span className="ink-muted">
            {posts.length} {posts.length === 1 ? 'post' : 'posts'}
          </span>
        ) : null}
      </div>
      {posts.length ? (
        <div className="ark-list" aria-label="Posts about this article">
          {posts.slice(0, SHOWN).map((p) => {
            const tone = TONE[p.reachStatus] || TONE.scheduled;
            const at = postTime(p);
            return (
              <Link key={p.id} to={calendarPostHref(p)} className="ark-post">
                <span className="rc-mark rc-mark-sm" title={channelName(p.platform)}>
                  {channelMark(p.platform)}
                </span>
                <span className="ark-post-text">
                  <span className="ark-post-words">{postText(p) || 'No words yet'}</span>
                  <span className="ark-post-meta">
                    <span className="ark-dot" style={{ background: tone.dot }} />
                    <span style={{ color: tone.ink }}>{tone.label}</span>
                    {at ? <span> · {dayTime(at)}</span> : null}
                  </span>
                </span>
              </Link>
            );
          })}
          {posts.length > SHOWN ? (
            <Link to="/reach/calendar" className="ink-link-btn ark-more">
              {posts.length - SHOWN} more in Social →
            </Link>
          ) : null}
        </div>
      ) : (
        <div className="ink-rail-note">
          {loading && !data
            ? 'Looking for posts…'
            : live
              ? 'No posts link to this article yet.'
              : 'Nothing yet. When a campaign shares new articles, your agent drafts posts once this goes live.'}
        </div>
      )}
      {/* Ask your agent first, or write it yourself (designer's notes, 2026-10-25). */}
      <AgentFirstButton
        action="articlePosts"
        ctx={{ title: article.title }}
        label="Write a post about this"
        className="ink-btn ark-write"
        wrapClassName="ark-write-wrap"
        align="left"
        onSelf={() => setWriting(true)}
      />
      <div className="ink-rail-note ark-hint">
        {live
          ? 'Opens with the link, summary and main image filled in.'
          : goesLive
            ? 'Not live yet, so the post starts an hour after it goes live.'
            : 'Not live yet. Save the post as a draft until it is.'}
      </div>
      {writing ? (
        <WritePost
          link={data?.reachArticlePosts.link || ''}
          linkTarget={{ kind: 'article', title: article.title }}
          text={plain(article.summary) || plain(article.title)}
          image={imageFor({ kind: 'article', title: article.title, imageUrl: article.imageUrl })}
          when={goesLive ? 'later' : 'draft'}
          at={goesLive ? new Date(goesLive.getTime() + HOUR_MS) : null}
          onClose={() => setWriting(false)}
          onPosted={() => {
            setWriting(false);
            void refetch();
          }}
        />
      ) : null}
    </div>
  );
}
